export type AppErrorCode =
  | "UNKNOWN"
  | "NETWORK_ERROR"
  | "INVALID_CREDENTIALS"
  | "AUTH_REQUIRED"
  | "SESSION_EXPIRED"
  | "RECOVERY_REQUEST_FAILED"
  | "RECOVERY_LINK_INVALID"
  | "PASSWORD_UPDATE_FAILED"
  | "PROFILE_NOT_FOUND"
  | "PROFILE_INACTIVE"
  | "PROFILE_DUPLICATE"
  | "PROFILE_ACCESS_DENIED"
  | "PROFILE_NETWORK_ERROR"
  | "PROFILE_LOAD_FAILED"
  | "API_ERROR"
  | (string & {});

export interface AppErrorOptions {
  code: AppErrorCode;
  userMessage: string;
  technicalMessage?: string;
  status?: number;
  retryable?: boolean;
  cause?: unknown;
}

export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly userMessage: string;
  readonly technicalMessage?: string;
  readonly status?: number;
  readonly retryable: boolean;
  override readonly cause?: unknown;

  constructor(options: AppErrorOptions) {
    super(options.userMessage);
    this.name = "AppError";
    this.code = options.code;
    this.userMessage = options.userMessage;
    this.technicalMessage = options.technicalMessage;
    this.status = options.status;
    this.retryable = options.retryable ?? false;
    this.cause = options.cause;
  }
}

export class ApiError extends AppError {
  constructor(options: AppErrorOptions) {
    super(options);
    this.name = "ApiError";
  }
}

export class AuthError extends AppError {
  constructor(options: AppErrorOptions) {
    super(options);
    this.name = "AuthError";
  }
}

function technicalMessage(error: unknown) {
  if (error instanceof Error) return error.name + ": " + error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown non-serializable error";
  }
}

export function isDevelopmentRuntime() {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
}

export function reportTechnicalError(error: unknown, context: string) {
  if (!isDevelopmentRuntime()) return;
  const appError = error instanceof AppError ? error : null;
  console.error("[" + context + "]", {
    code: appError?.code ?? "UNKNOWN",
    status: appError?.status,
    retryable: appError?.retryable,
    technicalMessage: appError?.technicalMessage ?? technicalMessage(error),
  });
}

export function toAppError(
  error: unknown,
  fallback: Partial<AppErrorOptions> = {},
): AppError {
  if (error instanceof AppError) return error;
  return new AppError({
    code: fallback.code ?? "UNKNOWN",
    userMessage: fallback.userMessage ?? "操作暂时无法完成，请稍后重试",
    technicalMessage: fallback.technicalMessage ?? technicalMessage(error),
    status: fallback.status,
    retryable: fallback.retryable ?? true,
    cause: error,
  });
}

export function mapAuthError(error: unknown, fallbackCode: AppErrorCode = "AUTH_REQUIRED") {
  if (error instanceof AuthError) return error;
  const raw = error as { code?: string; status?: number; message?: string } | null;
  const code = raw?.code?.toLowerCase() ?? "";
  const message = raw?.message?.toLowerCase() ?? "";

  if (code === "invalid_credentials" || message.includes("invalid login credentials")) {
    return new AuthError({
      code: "INVALID_CREDENTIALS",
      userMessage: "邮箱或密码不正确",
      technicalMessage: technicalMessage(error),
      status: 400,
      retryable: true,
      cause: error,
    });
  }
  if (
    code === "over_request_rate_limit"
    || code === "over_email_send_rate_limit"
    || message.includes("rate limit")
  ) {
    return new AuthError({
      code: "AUTH_RATE_LIMITED",
      userMessage: "请求过于频繁，请稍后再试",
      technicalMessage: technicalMessage(error),
      status: 429,
      retryable: true,
      cause: error,
    });
  }
  if (
    error instanceof TypeError
    || message.includes("failed to fetch")
    || message.includes("network")
  ) {
    return new AuthError({
      code: "NETWORK_ERROR",
      userMessage: "网络连接异常，请检查网络后重试",
      technicalMessage: technicalMessage(error),
      retryable: true,
      cause: error,
    });
  }
  return new AuthError({
    code: fallbackCode,
    userMessage: fallbackCode === "PASSWORD_UPDATE_FAILED"
      ? "新密码暂时无法保存，请重新打开最新恢复链接后再试"
      : "登录暂时无法完成，请稍后重试",
    technicalMessage: technicalMessage(error),
    status: raw?.status,
    retryable: true,
    cause: error,
  });
}
